// Persistenza foto scattate dalla squadra, così da non perderle se l'app
// viene chiusa/ricaricata prima dell'invio del report.
// Usiamo IndexedDB (adatto ai blob/binari, a differenza di localStorage).
// Le foto sono indicizzate per callId, in modo che non si mescolino tra
// chiamate diverse.

const DB_NAME = 'gcs_photos';
const STORE = 'photos';
const VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Compone la chiave univoca foto+chiamata
function keyFor(callId: string, photoId: string) {
  return `${callId}::${photoId}`;
}

export interface StoredPhoto {
  key: string;
  callId: string;
  photoId: string;
  name: string;
  type: string;
  blob: Blob;
}

export async function savePhoto(callId: string, file: File): Promise<void> {
  const db = await openDB();
  const photoId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const record: StoredPhoto = {
    key: keyFor(callId, photoId),
    callId,
    photoId,
    name: file.name,
    type: file.type || 'image/jpeg',
    blob: file,
  };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadPhotos(callId: string): Promise<File[]> {
  const db = await openDB();
  const result: File[] = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      const all = (req.result as StoredPhoto[]) || [];
      const files = all
        .filter((r) => r.callId === callId)
        .map((r) => new File([r.blob], r.name, { type: r.type }));
      resolve(files);
    };
    req.onerror = () => reject(req.error);
  });
  db.close();
  return result;
}

export async function clearPhotos(callId: string): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      const keys = (req.result as string[]) || [];
      keys
        .filter((k) => k.startsWith(`${callId}::`))
        .forEach((k) => store.delete(k));
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
  db.close();
}
